import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  makeStyles,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { UserAvatar } from "./ChatAvatar";
import { isDirectChat, otherMembers } from "./chatUtils";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accentText = isDark ? "#8DB8F5" : "#1A4FA8";
  const accentBg = isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC";
  return {
    paper: {
      width: 540,
      maxWidth: "100%",
      borderRadius: 14,
      color: theme.palette.text.primary,
      [theme.breakpoints.down("xs")]: { borderRadius: 0 },
    },
    head: {
      padding: theme.spacing(2.5, 3, 1),
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
    },
    title: { fontSize: 18, fontWeight: 700, margin: 0 },
    subtitle: { fontSize: 13, color: theme.palette.text.secondary },
    content: {
      padding: theme.spacing(1.5, 3, 0),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.75),
      flex: 1,
      minHeight: 0,
    },
    segmented: {
      display: "flex",
      padding: 4,
      gap: 4,
      borderRadius: 10,
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#F1F4F8",
    },
    segment: {
      flex: 1,
      height: 36,
      border: 0,
      borderRadius: 8,
      background: "transparent",
      color: theme.palette.text.secondary,
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    segmentActive: {
      backgroundColor: theme.palette.background.paper,
      color: accentText,
      fontWeight: 700,
      boxShadow: "0 1px 2px rgba(16,24,40,0.12)",
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    chips: { display: "flex", flexWrap: "wrap", gap: 6 },
    chip: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      height: 28,
      padding: "0 2px 0 4px",
      borderRadius: 14,
      backgroundColor: accentBg,
      color: accentText,
      fontSize: 12,
      fontWeight: 600,
    },
    chipRemove: { padding: 2, color: accentText },
    search: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      height: 40,
      padding: theme.spacing(0, 1.5),
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
      "&:focus-within": { borderColor: theme.palette.primary.main },
    },
    users: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      height: 240,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
      [theme.breakpoints.down("xs")]: { height: "auto", flex: 1, minHeight: 200 },
    },
    userRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(0.75, 1.5),
      cursor: "pointer",
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: 0 },
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    userRowSelected: {
      backgroundColor: isDark ? "rgba(45,123,232,0.12)" : "#F5F8FE",
    },
    userText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    },
    userName: { fontSize: 13, fontWeight: 600 },
    userMeta: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    centered: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    footer: {
      padding: theme.spacing(2, 3, 2.5),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
    note: {
      flex: 1,
      minWidth: 180,
      fontSize: 12,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("xs")]: { flexBasis: "100%" },
    },
    cancel: {
      [theme.breakpoints.down("xs")]: { marginLeft: "auto" },
    },
  };
});

export default function ChatModal({
  open,
  type,
  chat,
  chats,
  onClose,
  onSaved,
  onOpenExisting,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const isEdit = type === "edit";

  const [mode, setMode] = useState("direct");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTitleError(false);
    if (isEdit && chat) {
      setMode("group");
      setTitle(chat.title || "");
      setSelected(otherMembers(chat, user.id).map((u) => u.userId));
    } else {
      setMode("direct");
      setTitle("");
      setSelected([]);
    }
    // só ao abrir: atualizações do chat via socket não podem apagar o que
    // a pessoa está digitando
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, chat?.id, user.id]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoadingUsers(true);
    api
      .get("/users/list")
      .then(({ data }) => {
        if (active) setUsers(data.filter((u) => u.id !== user.id));
      })
      .catch((err) => active && toastError(err))
      .finally(() => active && setLoadingUsers(false));
    return () => {
      active = false;
    };
  }, [open, user.id]);

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = [...users].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "pt-BR")
    );
    if (!term) return list;
    return list.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(term) ||
        (u.queues || []).some((q) => (q.name || "").toLowerCase().includes(term))
    );
  }, [users, search]);

  const selectedUsers = useMemo(
    () => selected.map((id) => users.find((u) => u.id === id)).filter(Boolean),
    [selected, users]
  );

  const changeMode = (next) => {
    setMode(next);
    if (next === "direct") setSelected((prev) => prev.slice(0, 1));
  };

  const toggleUser = (id) => {
    if (mode === "direct") {
      setSelected((prev) => (prev[0] === id ? [] : [id]));
      return;
    }
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const canSave =
    !saving &&
    (mode === "direct" ? selected.length === 1 : selected.length >= 1);

  const handleSave = async () => {
    if (!canSave) return;
    const payloadUsers = selectedUsers.map((u) => ({ id: u.id, name: u.name }));

    if (mode === "direct") {
      const targetId = selected[0];
      const existing = (chats || []).find(
        (c) =>
          isDirectChat(c) &&
          otherMembers(c, user.id)[0]?.userId === targetId
      );
      if (existing) {
        onOpenExisting(existing);
        onClose();
        return;
      }
    } else if (!title.trim()) {
      setTitleError(true);
      return;
    }

    setSaving(true);
    try {
      const body = {
        title: mode === "group" ? title.trim() : "",
        users: payloadUsers,
      };
      const { data } = isEdit
        ? await api.put(`/chats/${chat.id}`, body)
        : await api.post("/chats", body);
      onSaved(data, isEdit);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const userMeta = (u) => {
    const queues = (u.queues || []).map((q) => q.name).filter(Boolean);
    const status = u.online ? "online" : "offline";
    return queues.length ? `${queues.join(", ")} · ${status}` : status;
  };

  const saveLabel = isEdit
    ? "Salvar"
    : mode === "direct"
    ? "Abrir conversa"
    : "Criar grupo";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      classes={{ paper: classes.paper }}
      aria-labelledby="chat-modal-title"
    >
      <div className={classes.head}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          <h2 id="chat-modal-title" className={classes.title}>
            {isEdit ? "Editar grupo" : "Nova conversa"}
          </h2>
          <span className={classes.subtitle}>
            {isEdit
              ? "Altere o nome ou os participantes do grupo."
              : "Escolha uma pessoa para conversa direta ou várias para um grupo."}
          </span>
        </div>
        <IconButton aria-label="Fechar" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.content}>
        {!isEdit && (
          <div className={classes.segmented} role="group" aria-label="Tipo de conversa">
            <button
              type="button"
              aria-pressed={mode === "direct"}
              className={`${classes.segment} ${
                mode === "direct" ? classes.segmentActive : ""
              }`}
              onClick={() => changeMode("direct")}
            >
              <PersonIcon style={{ fontSize: 18 }} />
              Conversa direta
            </button>
            <button
              type="button"
              aria-pressed={mode === "group"}
              className={`${classes.segment} ${
                mode === "group" ? classes.segmentActive : ""
              }`}
              onClick={() => changeMode("group")}
            >
              <GroupIcon style={{ fontSize: 18 }} />
              Grupo
            </button>
          </div>
        )}

        {mode === "group" && (
          <TextField
            id="chat-group-title"
            label="Nome do grupo"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError(false);
            }}
            error={titleError}
            helperText={titleError ? "Dê um nome ao grupo." : ""}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus={!isEdit}
            inputProps={{ maxLength: 80 }}
          />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <span className={classes.label} style={{ flex: 1 }}>
              {mode === "direct" ? "Com quem?" : "Participantes"}
            </span>
            {mode === "group" && selected.length > 0 && (
              <span className={classes.subtitle} style={{ fontSize: 12 }}>
                {selected.length === 1
                  ? "1 selecionado"
                  : `${selected.length} selecionados`}
              </span>
            )}
          </div>

          {mode === "group" && selectedUsers.length > 0 && (
            <div className={classes.chips}>
              {selectedUsers.map((u) => (
                <span key={u.id} className={classes.chip}>
                  <UserAvatar id={u.id} name={u.name} size={20} />
                  {u.name}
                  <IconButton
                    size="small"
                    className={classes.chipRemove}
                    aria-label={`Remover ${u.name}`}
                    onClick={() => toggleUser(u.id)}
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              ))}
            </div>
          )}

          <label className={classes.search}>
            <SearchIcon style={{ fontSize: 18, opacity: 0.6 }} />
            <InputBase
              placeholder="Buscar por nome ou departamento"
              inputProps={{ "aria-label": "Buscar pessoas" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, fontSize: 13 }}
              autoFocus={mode === "direct" && !isEdit}
            />
          </label>

          <div className={classes.users}>
            {loadingUsers && (
              <div className={classes.centered}>
                <CircularProgress size={22} />
              </div>
            )}
            {!loadingUsers && visibleUsers.length === 0 && (
              <div className={classes.centered}>Ninguém encontrado.</div>
            )}
            {!loadingUsers &&
              visibleUsers.map((u) => {
                const isSelected = selected.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className={`${classes.userRow} ${
                      isSelected ? classes.userRowSelected : ""
                    }`}
                  >
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleUser(u.id)}
                      inputProps={{ "aria-label": u.name }}
                      style={{ padding: 4 }}
                    />
                    <UserAvatar
                      id={u.id}
                      name={u.name}
                      size={34}
                      online={u.online}
                      showStatus
                    />
                    <span className={classes.userText}>
                      <span className={classes.userName}>{u.name}</span>
                      <span className={classes.userMeta}>{userMeta(u)}</span>
                    </span>
                  </label>
                );
              })}
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <Typography component="span" className={classes.note}>
          {mode === "group"
            ? "Você entra automaticamente no grupo."
            : "Se já existir conversa com essa pessoa, ela será aberta."}
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          className={classes.cancel}
          style={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disableElevation
          disabled={!canSave}
          style={{ textTransform: "none", fontWeight: 600, minWidth: 120 }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : saveLabel}
        </Button>
      </div>
    </Dialog>
  );
}
